/**
 * Staff Commission (BE-14d-1)
 *
 * Per commission recipient: earned and paid in the range, and what stands now - outstanding, payable (owed to them)
 * and receivable (paid ahead, owed back).
 * Route: /reports/staff-commission
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Grid, TextField, MenuItem } from '@mui/material';
import ReportPage, { formatCurrency, formatDate } from '../../components/accounting/ReportPage';
import { financeReportsService } from '../../services/accounting/financeReports.service';
import { toYmd } from '../../utils/accounting/periodUtils';
import { serverMessage } from '../../utils/apiConvert';

const TYPES = [
  { value: '', label: 'All' },
  { value: 'INTERNAL_STAFF', label: 'Staff' },
  { value: 'EXTERNAL_AGENT', label: 'External agent' },
  { value: 'EXTERNAL_PRO', label: 'External PRO' },
];
const typeLabel = (t) => TYPES.find((x) => x.value === t)?.label || t || '-';
const money = (v) => (Number(v) ? formatCurrency(v) : '-');

const StaffCommission = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  const [from, setFrom] = useState(toYmd(new Date(new Date().getFullYear(), new Date().getMonth(), 1)));
  const [to, setTo] = useState(toYmd(new Date()));
  const [type, setType] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setReport(await financeReportsService.getStaffCommission({ from, to, type }));
    } catch (err) {
      setReport(null);
      setError(serverMessage(err, 'Failed to load staff commission'));
    } finally {
      setLoading(false);
    }
  }, [from, to, type]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const t = report?.totals;
  const columns = [
    { key: 'name', label: 'Recipient' },
    { key: 'type', label: 'Type', render: (r) => typeLabel(r.type) },
    { key: 'earned', label: 'Earned (range)', align: 'right', render: (r) => money(r.earned), total: (x) => formatCurrency(x.earned) },
    { key: 'paid', label: 'Paid (range)', align: 'right', render: (r) => money(r.paid), total: (x) => formatCurrency(x.paid) },
    { key: 'outstanding', label: 'Outstanding (now)', align: 'right', render: (r) => money(r.outstanding), total: (x) => formatCurrency(x.outstanding) },
    { key: 'payable', label: 'Payable (now)', align: 'right', render: (r) => money(r.payable), total: (x) => formatCurrency(x.payable) },
    { key: 'receivable', label: 'Receivable (now)', align: 'right', render: (r) => money(r.receivable), total: (x) => formatCurrency(x.receivable) },
  ];

  const cards = t ? [
    { label: 'Earned (range)', value: formatCurrency(t.earned) },
    { label: 'Paid (range)', value: formatCurrency(t.paid), tone: 'good' },
    { label: 'Outstanding (now)', value: formatCurrency(t.outstanding), tone: Number(t.outstanding) > 0 ? 'warn' : undefined },
    { label: 'Payable (now)', value: formatCurrency(t.payable), tone: Number(t.payable) > 0 ? 'bad' : undefined },
    { label: 'Receivable (now)', value: formatCurrency(t.receivable) },
  ] : [];

  return (
    <ReportPage
      title="Staff Commission"
      subtitle={`Earned and paid ${formatDate(from)} – ${formatDate(to)}; outstanding, payable and receivable as of now`}
      exportType="staff-commission" exportParams={{ from, to, type }} onError={setError}
      onRefresh={fetchData} error={error} onErrorClose={() => setError(null)}
      loading={loading} cards={cards}
      columns={columns} rows={report?.rows || []} rowKey={(r) => r.recipientId}
      totals={t} totalsLabel={`TOTAL (${(report?.rows || []).length} recipients)`}
      emptyMessage="No commission recipient."
      filters={(
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6} sm={3}>
            <TextField fullWidth size="small" label="From" type="date" value={from}
              onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField fullWidth size="small" label="To" type="date" value={to}
              onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField select fullWidth size="small" label="Recipient type" value={type} onChange={(e) => setType(e.target.value)}>
              {TYPES.map((x) => <MenuItem key={x.value} value={x.value}>{x.label}</MenuItem>)}
            </TextField>
          </Grid>
        </Grid>
      )}
    />
  );
};

export default StaffCommission;
